import { ArrowUp } from "lucide-react";
import type { KeyboardEvent, ReactNode } from "react";
import type { MutableRefObject } from "react";
import { ShortcutKbd } from "@/core/components/ui/kbd";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { cn } from "@/core/utils/utils";

interface HarnessChatInputProps {
  draft: string;
  disabled: boolean;
  textareaRef: MutableRefObject<HTMLTextAreaElement | null>;
  onDraftChange: (value: string) => void;
  onSend: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  // Optional content rendered above the input pill (e.g. attached context chips).
  header?: ReactNode;
}

export const HarnessChatInput = ({
  draft,
  disabled,
  textareaRef,
  onDraftChange,
  onSend,
  onKeyDown,
  placeholder = "Ask anything about this payroll setup...",
  header,
}: HarnessChatInputProps) => (
  <div className="shrink-0 border-t border-stroke-tertiary px-4 py-3">
    {header}
    <Stack
      gap="none"
      align="stretch"
      fullWidth
      onMouseDown={(event) => {
        const target = event.target as HTMLElement;
        if (target.closest("button, textarea, input, a")) return;
        event.preventDefault();
        textareaRef.current?.focus();
      }}
      className="cursor-text rounded-3xl border border-input bg-card shadow-sm focus-within:ring-1 focus-within:ring-ring"
    >
      <textarea
        ref={textareaRef}
        value={draft}
        onChange={(event) => onDraftChange(event.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        rows={1}
        disabled={disabled}
        className={cn(
          "w-full resize-none bg-transparent px-4 pt-3.5 text-md text-primary-text",
          "placeholder:text-tertiary-text",
          "focus:outline-none disabled:opacity-50",
        )}
        style={{ minHeight: "44px", maxHeight: "160px" }}
      />
      <div className="flex items-center justify-end px-3 pb-3">
        <button
          type="button"
          onClick={onSend}
          disabled={!draft.trim() || disabled}
          className={cn(
            "flex h-8 w-8 items-center justify-center rounded-full transition-colors",
            {
              "bg-primary text-white hover:bg-primary/90":
                draft.trim() && !disabled,
              "bg-surface-secondary text-tertiary-text":
                !draft.trim() || disabled,
            },
          )}
          aria-label="Send"
        >
          <ArrowUp className="h-4 w-4" />
        </button>
      </div>
    </Stack>
    <Typography
      variant="captionW1"
      textColor="tertiary"
      className="mt-1.5 text-center"
    >
      <ShortcutKbd keys={["Enter"]} /> send ·{" "}
      <ShortcutKbd keys={["shift", "Enter"]} /> newline
    </Typography>
  </div>
);
